import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';

/**
 * DeleteAccountDialog — guarded "delete a user account" workflow used
 * from Org Settings → Members and (optionally) the Users tab.
 *
 * Two modes:
 *   • 🧹 clean    — only when the account has zero attached records.
 *                   Hard-deletes the user row so the email is freed
 *                   immediately for re-registration. Best for "wrong
 *                   sign-up" cases.
 *   • 🗄️ archive  — preserves all history (bookings, clock-events,
 *                   payments, etc.) by tombstoning the email and
 *                   marking archived=true. The original email becomes
 *                   free to re-register without orphaning records.
 *
 * Guardrails (UI side — backend re-validates):
 *   • Refuses to render for the current user, the org_owner, or empty.
 *   • Confirmation text must match the user's current email exactly.
 *   • Each mode button is disabled when not allowed (with explanation).
 */

const FUTURE_KEYS = new Set(['future_bookings', 'future_shifts']);

const COUNT_LABELS = [
  ['children', '👶 Children'],
  ['bookings', '📅 Total bookings'],
  ['future_bookings', '⏭️ Future bookings'],
  ['recurring_plans', '🔁 Recurring plans'],
  ['payments', '💳 Payments'],
  ['custody_events', '🛡️ Custody events'],
  ['messages', '💬 Messages'],
  ['staff_profile', '👨‍🏫 Staff profile'],
  ['shifts', '🕒 Total shifts'],
  ['future_shifts', '⏭️ Future shifts'],
  ['clock_events', '⏱️ Clock events'],
  ['timesheet_approvals', '📨 Timesheet approvals'],
  ['leave_requests', '🌴 Leave requests'],
  ['push_subscriptions', '🔔 Push subscriptions'],
  ['transport_manifests', '🚐 Transport manifests'],
];

export function DeleteAccountDialog({ userId, onClose, onDone }) {
  const [impact, setImpact] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(null); // 'clean' | 'archive'
  const [confirmEmail, setConfirmEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await api.getAccountImpact(userId);
        if (!cancelled) {
          setImpact(data);
          // Default mode hint — clean if allowed, otherwise archive.
          setMode(data.can_clean_delete ? 'clean' : (data.can_archive ? 'archive' : null));
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  if (!userId) return null;

  const handleSubmit = async () => {
    if (!mode) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await api.deleteAccount(userId, { mode, confirm_email: confirmEmail });
      const verb = result.outcome === 'clean_deleted' ? 'deleted' : 'archived';
      toast.success(`Account ${verb}`, {
        description: result.outcome === 'clean_deleted'
          ? `${result.freed_email} is now free for re-registration.`
          : `Email ${result.freed_email} freed; history preserved.`,
      });
      onDone?.(result);
      onClose?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const targetEmail = impact?.user?.email || '';
  const emailMatches = confirmEmail.trim().toLowerCase() === targetEmail.toLowerCase() && targetEmail.length > 0;

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="delete-account-dialog">
      <div className="modal-content" style={{ maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid hsl(var(--border))' }}>
          <h2 style={{ margin: 0 }}>🗑️ Delete account</h2>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }}>
            Frees the email for re-registration. Choose <strong>Clean delete</strong> for a brand-new account that signed up under the wrong role; choose <strong>Archive</strong> to preserve history while still freeing the email.
          </p>
        </div>

        <div style={{ padding: '1rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.85rem', maxHeight: '70vh', overflowY: 'auto' }}>
          {error && <div className="error-message" data-testid="delete-account-error">{error}</div>}
          {loading && <p>Loading account impact…</p>}

          {!loading && impact && (
            <>
              {/* Identity card */}
              <div className="card" style={{ background: 'hsl(var(--muted)/0.4)', padding: '0.75rem' }}>
                <div style={{ fontSize: '0.92rem' }}>
                  <strong>{impact.user.full_name || '(no name)'}</strong>
                </div>
                <div style={{ fontSize: '0.82rem', color: 'hsl(var(--muted-foreground))' }}>
                  {impact.user.email} · role: {impact.user.role}
                  {impact.user.archived ? ' · 🗄️ already archived' : ''}
                </div>
              </div>

              {/* Impact counts */}
              <div>
                <strong style={{ fontSize: '0.85rem' }}>Linked records</strong>
                <div style={{ fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))', marginBottom: 4 }}>
                  Total attached: {impact.total_attached_records}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 4 }} data-testid="delete-account-counts">
                  {COUNT_LABELS.map(([k, label]) => {
                    const v = impact.counts?.[k] || 0;
                    if (v === 0 && !FUTURE_KEYS.has(k)) return null; // Hide zero non-future rows for compactness
                    const isFuture = FUTURE_KEYS.has(k);
                    return (
                      <div
                        key={k}
                        data-testid={`delete-impact-${k}`}
                        style={{
                          fontSize: '0.78rem',
                          padding: '0.25rem 0.45rem',
                          borderRadius: 4,
                          background: v > 0 ? (isFuture ? 'hsl(0 70% 94%)' : 'hsl(45 95% 92%)') : 'transparent',
                          color: v > 0 ? (isFuture ? 'hsl(0 60% 35%)' : 'hsl(35 80% 30%)') : 'hsl(var(--muted-foreground))',
                          fontWeight: v > 0 ? 600 : 400,
                        }}
                      >
                        {label}: {v}
                      </div>
                    );
                  })}
                </div>
                {impact.counts?.future_bookings > 0 || impact.counts?.future_shifts > 0 ? (
                  <p style={{ fontSize: '0.78rem', color: 'hsl(0 60% 45%)', marginTop: 6 }}>
                    ⚠️ This user has future commitments. Reassign or cancel them before archiving so service isn't disrupted.
                  </p>
                ) : null}
              </div>

              {/* Mode picker */}
              <div>
                <strong style={{ fontSize: '0.85rem' }}>Delete mode</strong>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
                  <label
                    style={{
                      display: 'flex', gap: 8, alignItems: 'flex-start',
                      padding: '0.6rem 0.75rem',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: 8,
                      background: mode === 'clean' ? 'hsl(var(--primary)/0.06)' : 'transparent',
                      opacity: impact.can_clean_delete ? 1 : 0.55,
                      cursor: impact.can_clean_delete ? 'pointer' : 'not-allowed',
                    }}
                    data-testid="delete-mode-clean-row"
                  >
                    <input
                      type="radio"
                      name="delete-mode"
                      value="clean"
                      checked={mode === 'clean'}
                      onChange={() => setMode('clean')}
                      disabled={!impact.can_clean_delete}
                      data-testid="delete-mode-clean"
                      style={{ marginTop: 3 }}
                    />
                    <div>
                      <div style={{ fontSize: '0.88rem', fontWeight: 600 }}>🧹 Clean delete</div>
                      <div style={{ fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))' }}>
                        Hard-deletes the user. Email is freed instantly. Allowed when there are no operational records (shifts, clock-events, timesheets, leave, children, bookings, payments, or custody). A bare staff/driver profile is cleared automatically.
                      </div>
                      {!impact.can_clean_delete && (
                        <div style={{ fontSize: '0.74rem', color: 'hsl(0 60% 45%)', marginTop: 3 }}>
                          Not available — this account has operational or financial history. Use Archive instead.
                        </div>
                      )}
                    </div>
                  </label>

                  <label
                    style={{
                      display: 'flex', gap: 8, alignItems: 'flex-start',
                      padding: '0.6rem 0.75rem',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: 8,
                      background: mode === 'archive' ? 'hsl(var(--primary)/0.06)' : 'transparent',
                      opacity: impact.can_archive ? 1 : 0.55,
                      cursor: impact.can_archive ? 'pointer' : 'not-allowed',
                    }}
                    data-testid="delete-mode-archive-row"
                  >
                    <input
                      type="radio"
                      name="delete-mode"
                      value="archive"
                      checked={mode === 'archive'}
                      onChange={() => setMode('archive')}
                      disabled={!impact.can_archive}
                      data-testid="delete-mode-archive"
                      style={{ marginTop: 3 }}
                    />
                    <div>
                      <div style={{ fontSize: '0.88rem', fontWeight: 600 }}>🗄️ Archive (preserve history)</div>
                      <div style={{ fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))' }}>
                        Tombstones the email so the address is freed for re-registration. Children, bookings, payments, clock-events and audit history are kept intact. Their staff profile (if any) is deactivated.
                      </div>
                      {!impact.can_archive && (
                        <div style={{ fontSize: '0.74rem', color: 'hsl(0 60% 45%)', marginTop: 3 }}>
                          Already archived.
                        </div>
                      )}
                    </div>
                  </label>
                </div>
              </div>

              {/* Confirmation phrase */}
              <div className="form-group" style={{ margin: 0 }}>
                <label htmlFor="delete-confirm-email" style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                  Type <code style={{ background: 'hsl(var(--muted))', padding: '0 4px', borderRadius: 3 }}>{targetEmail}</code> to confirm
                </label>
                <input
                  id="delete-confirm-email"
                  type="email"
                  autoComplete="off"
                  value={confirmEmail}
                  onChange={(e) => setConfirmEmail(e.target.value)}
                  placeholder={targetEmail}
                  data-testid="delete-confirm-email"
                />
              </div>
            </>
          )}
        </div>

        <div style={{ padding: '0.85rem 1.25rem', borderTop: '1px solid hsl(var(--border))', display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={submitting} data-testid="delete-account-cancel">Cancel</button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={submitting || loading || !mode || !emailMatches}
            data-testid="delete-account-confirm"
            style={{
              background: mode === 'clean'
                ? 'hsl(0 70% 50%)'
                : 'hsl(var(--primary))',
            }}
          >
            {submitting
              ? 'Working…'
              : (mode === 'clean' ? '🧹 Clean delete' : mode === 'archive' ? '🗄️ Archive account' : 'Delete')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DeleteAccountDialog;
